/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactModal from "react-modal";
import { FlexBetweenContainer } from "../../../../containers/FlexBetweenContainer";
import { useGetTokenMetadataBySymbol } from "../../../../hooks/useTokenMetadataBySymbol";
import { ReactComponent as RemoveTradeManyIcon } from "../../../../img/cancel_filled.svg";
import { ReactComponent as CloseIcon } from "../../../../img/close.svg";
import { ReactComponent as ResetIcon } from "../../../../img/reset-icon.svg";
import { ReactComponent as SearchIcon } from "../../../../img/search-icon.svg";
import { ReactComponent as TokenBanner } from "../../../../img/token_selector/trade-many-desktop.svg";
import { GlobalStore } from "../../../../store/global/GlobalStore";
import { Token } from "../../../../store/global/TokenEnum";
import { Balances } from "../../../../store/global/getBalances";
import { Amount } from "../../../../types/amount";
import { getSelectedTokenSymbolArray } from "../../../../utils/quoteUtils";
import { getTokenIcon } from "../../../../utils/tokenIconUtils";
import { getNameForToken, isPricingAvailable } from "../../../../utils/tokenMetadataUtils";
import {
  getTokenRowDisabledReason,
  isTokenAvailable,
  sortTokensByAmount,
} from "../../../../utils/tokenSelectorUtils";
import { ModalContent } from "../../../general/ModalContent/ModalContent";
import { Text } from "../../../general/Text";
import "../../modal.css";
import { NoTokenMatches } from "./NoTokenMatches";
import "./SelectTokenModal.css";
import { SelectTokensButton } from "./SelectTokensButton";
import { TokenRow } from "./TokenRow";
// Element does not exist in Jest testing - so check if it exists and set it conditionally
const rootElement = document.getElementById("root");
// Hide root element from screen-readers
ReactModal.setAppElement(rootElement ? "#root" : "*");

export type TokenSelectMode = "single" | "multiple" | "none";
export type Counterparty = "maker" | "taker";
export type Direction = "buy" | "sell";

export interface TokenSelectModalProps {
  isOpen: boolean;
  tokenSelectMode: TokenSelectMode;
  currentSymbol?: Token;
  counterParty: Counterparty;
  onSelected: (tokens: Array<Token>) => void;
  onClose: () => void;
}

export const TokenSelectModal: React.FC<TokenSelectModalProps> = props => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const searchField = useRef<HTMLInputElement | null>(null);
  const getTokenMetadataBySymbol = useGetTokenMetadataBySymbol();
  const [selectedTokens, setSelectedTokens] = useState<Array<Token>>([]);
  const [tokenSearchString, setTokenSearchString] = useState<string>("");
  const [showTokenBanner, setShowTokenBanner] = useState<boolean>(true);
  const { makerTokens, takerTokens, network, tokensMetadata, balances } = GlobalStore.useState(
    s => {
      return {
        makerTokens: s.makerTokens,
        takerTokens: s.takerTokens,
        network: s.network,
        tokensMetadata: s.tokens,
        balances: s.balances,
      };
    }
  );

  // Memoize Counterparty tokens - otherwise value will change on every re-render which causes infinite loops
  const counterPartyTokens = useMemo(
    () => (props.counterParty === "taker" ? takerTokens : makerTokens).map(e => e.token),
    [props.counterParty, makerTokens, takerTokens]
  );
  const takerTokensArray = useMemo(() => getSelectedTokenSymbolArray(takerTokens), [takerTokens]);
  const makerTokensArray = useMemo(() => getSelectedTokenSymbolArray(makerTokens), [makerTokens]);

  // Updated selected values on prop changes
  useEffect(() => {
    setSelectedTokens(counterPartyTokens);
  }, [counterPartyTokens]);

  const searchTokens = useCallback(
    (searchString: string) => {
      setTokenSearchString(searchString);
    },
    [setTokenSearchString]
  );

  const handleMultipleSelect = useCallback(
    (token: Token) => {
      const isRemoving = selectedTokens.includes(token);
      if (isRemoving) {
        setSelectedTokens(selectedTokens.filter(value => value !== token));
        return;
      }
      setSelectedTokens(prevTokens => {
        return [...prevTokens, token];
      });
    },
    [selectedTokens]
  );
  const handleSelect = useCallback(
    (token: Token) => {
      if (props.tokenSelectMode === "multiple") {
        handleMultipleSelect(token);
      } else {
        const newTokens = counterPartyTokens.filter(entry => {
          return entry !== props.currentSymbol;
        });
        props.onSelected([...newTokens, token]);
      }
    },
    [counterPartyTokens, handleMultipleSelect, props]
  );

  const handleClose = useCallback(() => {
    setTokenSearchString("");
    setSelectedTokens(counterPartyTokens);
    GlobalStore.update(s => {
      s.modalSelector = undefined;
    });
    props.onClose();
  }, [counterPartyTokens, props]);

  const filterTokens = useCallback(
    (list: Balances, predicate: string): Balances => {
      const predicateLowerCase = predicate.toLowerCase();
      const oppositeCPTokens = (props.counterParty === "maker" ? takerTokens : makerTokens).map(
        entry => entry.token
      );
      const allTokens = [...makerTokens.map(e => e.token), ...takerTokens.map(e => e.token)];
      const nonSelectedTokens = list.filter(entry => {
        if (props.tokenSelectMode === "single") {
          return !allTokens.includes(entry.symbol);
        } else {
          // If we are selecting multiples - we still show the selected counterPartyTokens in the list,
          // but we don't show the selected counterparty token
          return !oppositeCPTokens.includes(entry.symbol);
        }
      });
      //Filter native (non-wrapped) counterPartyTokens
      return nonSelectedTokens.filter(
        entry =>
          !predicate ||
          entry.symbol.toLowerCase().includes(predicateLowerCase) ||
          getTokenMetadataBySymbol(entry.symbol)?.name.toLowerCase().includes(predicateLowerCase)
      );
    },
    [props.counterParty, props.tokenSelectMode, takerTokens, makerTokens, getTokenMetadataBySymbol]
  );

  const title = useMemo(() => {
    if (props.currentSymbol) {
      return props.tokenSelectMode === "single"
        ? `Replace ${props.currentSymbol}`
        : "Select Tokens";
    }
    return props.tokenSelectMode === "single" ? "Select Token" : `Select Tokens`;
  }, [props.currentSymbol, props.tokenSelectMode]);
  const sortedFilteredBalances = sortTokensByAmount(
    filterTokens(balances, tokenSearchString),
    tokensMetadata
  );
  const showSelectButton =
    props.tokenSelectMode === "multiple" &&
    selectedTokens.length > 0 &&
    sortedFilteredBalances.length !== 0;

  return (
    <ReactModal
      isOpen={props.isOpen}
      overlayClassName={"modal select-token-modal"}
      className={`react-modal`}
      style={{ content: { width: "100%" } }}
      shouldCloseOnOverlayClick={true}
      onRequestClose={handleClose}
    >
      {/*modal-body token-modal*/}
      <ModalContent css={tokenModalCss} data-testid="token-select-modal">
        <FlexBetweenContainer>
          <Text
            size="Medium"
            textStyle="Title"
            as="h5"
            textCss={css`
              margin: 0;
              width: 100%;
              text-align: center;
            `}
          >
            {title}
          </Text>
          <ModalCloseButton
            onClick={handleClose}
            aria-label="Close"
            data-testid="token-select-modal-close-btn"
          >
            <CloseIcon width={10} height={10} />
          </ModalCloseButton>
        </FlexBetweenContainer>
        <SearchContainer>
          <TokenSelectorSearchInput
            autoFocus={!isMobile}
            type="text"
            placeholder="Search..."
            // Google Tag manager fires an event to track in Google analytics when this is clicked
            id="name_input"
            ref={searchField}
            onChange={e => searchTokens(e.target.value)}
          />
          <SearchIcon css={searchIconCss} />
          <ResetIcon
            css={resetSearchIconCss(tokenSearchString)}
            onClick={() => {
              if (searchField?.current?.value) {
                searchField.current.value = "";
                searchField.current?.focus();
              }
              setTokenSearchString("");
            }}
          />
        </SearchContainer>
        <TokenRowWrapper hasButton={showSelectButton}>
          {showTokenBanner && (
            <TradeManyBannerContainer>
              <TokenBanner />
              <RemoveTradeManyBannerButton
                aria-label="Close"
                onClick={() => setShowTokenBanner(false)}
              >
                <RemoveTradeManyIcon />
              </RemoveTradeManyBannerButton>
            </TradeManyBannerContainer>
          )}
          <NoTokenMatches isVisible={sortedFilteredBalances.length === 0} />
          {sortedFilteredBalances.map(entry => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            const hasPrice = isPricingAvailable(
              tokensMetadata,
              entry.symbol,
              props.counterParty === "maker" ? "buy" : "sell"
            );
            const price: Amount = tokensMetadata
              ? tokensMetadata[entry.symbol]?.priceUsd ?? Amount.ZERO
              : Amount.ZERO;
            const isSelected = selectedTokens.some(
              selectedSymbol => selectedSymbol === entry.symbol
            );
            const name = getNameForToken(entry.symbol, tokensMetadata);
            const tokenDisabledReason = getTokenRowDisabledReason(
              entry.symbol,
              network,
              props.counterParty,
              takerTokensArray,
              makerTokensArray,
              tokensMetadata,
              selectedTokens,
              hasPrice,
              props.tokenSelectMode
            );
            return (
              <TokenRow
                tokenSelectMode={props.tokenSelectMode}
                tokenMetaData={tokensMetadata}
                isSelected={isSelected}
                key={entry.symbol}
                symbol={entry.symbol}
                name={name ? name : ""}
                balance={entry.balance}
                tokenIcon={getTokenIcon(entry.symbol, tokensMetadata)}
                isDisabled={tokenDisabledReason != null}
                tokenDisabledReason={tokenDisabledReason}
                isUnavailable={!(hasPrice && isTokenAvailable(tokensMetadata, entry.symbol))}
                price={price}
                onTokenSelect={symbol => {
                  handleSelect(symbol);
                }}
                selectedTokens={selectedTokens}
                counterParty={props.counterParty}
              />
            );
          })}
        </TokenRowWrapper>
        <SelectTokensButton
          visible={showSelectButton}
          numberOfTokens={selectedTokens.length}
          onClick={() => {
            props.onSelected(selectedTokens);
            props.onClose();
          }}
        />
      </ModalContent>
    </ReactModal>
  );
};

// Sub components

export const searchIconCss = css`
  position: absolute;
  top: 25px;
  left: 32px;
`;
export const resetSearchIconCss = (tokenSearchString: string) => css`
  position: absolute;
  right: 32px;
  top: 28px;
  cursor: pointer;
  display: ${tokenSearchString ? "block" : "none"};
`;
export const tokenModalCss = css`
  padding: 18px 0 0;
  max-height: calc(100dvh - 68px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const TokenSelectorSearchInput = styled.input`
  background: #000000;
  border-radius: 4px;
  border: none;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
  color: white;
  height: 38px;
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem 0.375rem 40px;
  width: 100%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  outline: 0; // Overwrite user agent style
  font-family: ${props => props.theme.fontFamilies["work-sans"]};

  ::placeholder {
    color: #748b8d;
  }
`;

export const SearchContainer = styled.div`
  padding: 20px;
  border-bottom: 1px solid #21373a;
  position: relative;
  display: flex;
  align-items: center;
`;

export const TokenRowWrapper = styled.div<{ hasButton: boolean }>`
  padding: 0;
  height: ${({ hasButton }) => (hasButton ? "calc(100vh - 250px)" : "80vh")};
  overflow-x: hidden;
  /* Style scroll bar for firefox */
  scrollbar-width: thin;
`;

export const TradeManyBannerContainer = styled.div`
  margin: 10px 19px 19px 19px;
  position: relative;
  border: 1px solid ${props => props.theme.theme["700"]};
  border-radius: 12px;
  overflow: clip;
`;

export const RemoveTradeManyBannerButton = styled.button`
  top: 10%;
  right: 2%;
  position: absolute;
  padding: 0;
  box-sizing: content-box;
  width: 24px;
  height: 24px;
  color: #000;
  border: 0;
  display: flex;
  background: none;
`;
export const ModalCloseButton = styled.button`
  padding: 0;
  border: 0;
  position: absolute;
  right: 20px;
  fill: ${props => props.theme.button.secondary.surface.default};
  background: none;
`;
